import { D3DragEvent, SubjectPosition, drag, select } from "d3";
import { RefObject, useCallback, useEffect, useMemo, useState } from "react";

import { usePlayContext } from "../../../pages/Play/play-context";
import { ActionType, MarkerType, MidPoint, PointType, StencilType } from "../../../types";
import { usePlayingSurfaceContext } from "../../playing-surface/playing-surface-context";
import { StencilWrapperProps } from "../stencil-wrapper";
import { clearAssociationCircleHighlights, getComparisonStencils, highlightAssociatedStencils } from "../utils";

type SelectPointElement = SVGRectElement | SVGCircleElement;
type DragPointElement = D3DragEvent<SelectPointElement, PointData, SubjectPosition>;

interface PointData {
  pointIndex: number;
  pointType: PointType;
  x: number;
  y: number;
}

interface UpdateTempControlPointsProps {
  type: ActionType | MarkerType;
  pointIndex: number;
  x: number;
  y: number;
}
type UpdateTempControlPointsFunc = (props: UpdateTempControlPointsProps) => void;

export type UseMoveStencilNodePointProps = Pick<StencilWrapperProps, "controlPoints" | "stencil"> & {
  stencilWrapperRef: RefObject<SVGGElement>;
};

export const useMoveStencilNodePoint = ({
  stencil,
  stencilWrapperRef,
  controlPoints: controlPointsOrUndefined,
}: UseMoveStencilNodePointProps) => {
  const controlPoints = useMemo(() => controlPointsOrUndefined || [], [controlPointsOrUndefined]);
  const { id } = stencil;

  const { selectItemApi, frameStencilsApi, playStateApi } = usePlayContext();
  const { selectItem } = selectItemApi;
  const { updateStencilControlPoints } = frameStencilsApi;
  const { activeFrame } = playStateApi;

  const frameStencils = useMemo(() => activeFrame?.frameStencils ?? [], [activeFrame]);

  const { isEditable } = usePlayingSurfaceContext();

  const [tempControlPoints, setTempControlPoints] = useState<MidPoint[]>(controlPoints);

  useEffect(() => {
    setTempControlPoints(controlPoints);
  }, [controlPoints]);

  const updateTempControlPoints: UpdateTempControlPointsFunc = useCallback(({ type, pointIndex, x, y }) => {
    setTempControlPoints((prevState) => {
      switch (type) {
        case MarkerType.SHADING_RECTANGLE:
        case MarkerType.SHADING_OVAL: {
          const newPoint: MidPoint = { ...prevState[pointIndex], x, y } as MidPoint;

          switch (pointIndex) {
            case 0:
              return [newPoint, { ...prevState[1], y }, prevState[2], { ...prevState[3], x }] as MidPoint[];
            case 1:
              return [{ ...prevState[0], y }, newPoint, { ...prevState[2], x }, prevState[3]] as MidPoint[];
            case 2:
              return [prevState[0], { ...prevState[1], x }, newPoint, { ...prevState[3], y }] as MidPoint[];
            case 3:
              return [{ ...prevState[0], x }, prevState[1], { ...prevState[2], y }, newPoint] as MidPoint[];
            default:
              return prevState;
          }
        }

        default: {
          const pointToMove = prevState.find((_point, i) => i === pointIndex);
          if (pointToMove) {
            const updatedPoint = { ...pointToMove, x, y };
            return [...prevState.slice(0, pointIndex), updatedPoint, ...prevState.slice(pointIndex + 1)];
          } else return prevState;
        }
      }
    });
  }, []);

  useEffect(() => {
    const g = stencilWrapperRef.current;

    if (g && isEditable) {
      const { stencilType } = stencil;
      const comparisonStartStencils = getComparisonStencils({ frameStencils, stencil, nodePoint: "start" });
      const comparisonEndStencils = getComparisonStencils({ frameStencils, stencil, nodePoint: "end" });

      const startPos = stencilType === StencilType.ACTION ? stencil.pos : stencil.pos;

      const type = stencilType === StencilType.ACTION ? stencil.actionType : stencil.markerType;
      const points = select<SVGGElement, unknown>(g).selectAll<SelectPointElement, PointData>(".select-point");

      const pointData: PointData[] = points.nodes().map((node) => {
        const d3Node = select<SelectPointElement, unknown>(node);
        const pointIndex = Number(d3Node.attr("data-point-index"));
        const pointType = d3Node.attr("data-point-type") as PointType;

        return {
          pointIndex,
          pointType,
          x: Number(node.getAttribute("x") || node.getAttribute("cx") || 0),
          y: Number(node.getAttribute("y") || node.getAttribute("cy") || 0),
        };
      });

      points.data(pointData);

      points.call(
        drag<SelectPointElement, PointData>()
          .subject((event: DragPointElement) => ({ x: event.x, y: event.y }))
          .on("start", function () {
            selectItem(id);
          })
          .on("drag", function (event: DragPointElement, d) {
            const { pointIndex } = d;
            const { x, y, subject } = event;
            const { x: subjectX, y: subjectY } = subject;

            const newX = x + startPos.x;
            const newY = y + startPos.y;

            const pointStartPos = pointData.find((point) => point.pointIndex === pointIndex);
            const pointStartX = pointStartPos?.x ?? 0;
            const pointStartY = pointStartPos?.y ?? 0;

            if (pointIndex === 0) {
              highlightAssociatedStencils({ ...comparisonStartStencils, newX, newY });
            } else if (pointIndex === pointData.length - 1) {
              highlightAssociatedStencils({ ...comparisonEndStencils, newX, newY });
            }

            const mouseDiffX = subjectX - pointStartX;
            const mouseDiffY = subjectY - pointStartY;

            updateTempControlPoints({ type, pointIndex, x: x - mouseDiffX, y: y - mouseDiffY });
          })
          .on("end", function (event: DragPointElement, d) {
            const { pointIndex } = d;
            const { x, y, subject } = event;
            const { x: subjectX, y: subjectY } = subject;

            const deltaX = x - subjectX;
            const deltaY = y - subjectY;

            clearAssociationCircleHighlights();

            if (deltaX !== 0 || deltaY !== 0) updateStencilControlPoints({ id, pointIndex, deltaX, deltaY });
          })
      );
    } else if (g && !isEditable) {
      const points = select<SVGGElement, unknown>(g).selectAll<SelectPointElement, PointData>(".select-point");

      points.on("mousedown.drag", null);
    }
  }, [
    stencilWrapperRef,
    id,
    isEditable,
    selectItem,
    updateTempControlPoints,
    updateStencilControlPoints,
    stencil,
    frameStencils,
  ]);

  return { tempControlPoints };
};
